/**
 * Image cache system
 *
 * Two-level LRU cache for efficient image caching
 */

import type { SizeConstraints, SizeSpec } from '@geom';
import { calculateFittedSize, MIP, Size } from '@geom';
import { LRUCache } from 'lru-cache';
import { Image } from './Image';
import type { ImageSource } from './ImageSource';

/**
 * Image cache level 1 entry (image sources)
 */
export interface ImageCache1Entry {
  imageSource: ImageSource;
}

/**
 * Image cache level 2 entry (rendered images)
 */
export interface ImageCache2Entry {
  image: Image;
}

/**
 * Two-level LRU cache for images
 *
 * Similar to ParagraphCache, this provides:
 * - Level 1: Image sources
 * - Level 2: Rendered CanvasKit Image objects at different scales
 */
export class ImageCache {
  #cache1: LRUCache<string, ImageCache1Entry>;
  #cache2: LRUCache<string, ImageCache2Entry>;

  constructor() {
    this.#cache1 = new LRUCache({
      max: 5000,
      dispose: entry => {
        // When a source is evicted from cache1, clean up all its rendered images from cache2
        const sourceId = entry.imageSource.id;
        const keysToDelete = Array.from(this.#cache2.keys()).filter(key =>
          key.startsWith(`${sourceId}-`)
        );
        keysToDelete.forEach(key => this.#cache2.delete(key));
      }
    });

    this.#cache2 = new LRUCache({
      max: 1000,
      dispose: entry => {
        // When a rendered image is evicted, properly clean up the CanvasKit Image
        entry.image.delete();
      }
    });
  }

  private static cache2Key(imageSource: ImageSource, sizeSpec?: SizeSpec): string {
    if (!sizeSpec) {
      return `${imageSource.id}-original`;
    }

    // Handle direct Size objects or SizeConstraints
    let cacheSize: Size;
    if (sizeSpec instanceof Size) {
      // Direct Size specification - cache at exact size
      cacheSize = sizeSpec;
    } else {
      // SizeConstraints - calculate the cache size using MIP strategy
      // Use MIP.DOWN_OR_UP for scalable sources (vector formats), MIP.DOWN for pixel formats
      const mipStrategy = imageSource.scalable ? MIP.DOWN_OR_UP : MIP.DOWN;
      cacheSize = calculateFittedSize(imageSource.intrinsicSize, sizeSpec, mipStrategy);
    }

    return `${imageSource.id}-${Math.round(cacheSize.width)}x${Math.round(cacheSize.height)}`;
  }

  set(imageSource: ImageSource): void {
    if (!imageSource.isLoaded) {
      throw new Error(`Cannot cache non-loaded ImageSource: ${imageSource.toString()}`);
    }

    this.#cache1.set(imageSource.id, {
      imageSource
    });
  }

  get(sourceId: string, sizeSpec?: SizeSpec): Image | undefined {
    const entry1 = this.#cache1.get(sourceId);
    if (entry1 === undefined) {
      return undefined;
    }

    const key = ImageCache.cache2Key(entry1.imageSource, sizeSpec);

    // Check if we already have this rendered size cached
    let entry2 = this.#cache2.get(key);
    if (!entry2) {
      // Render at MIP-adjusted cache size and cache the result
      let renderSize: Size | SizeConstraints | undefined = sizeSpec;

      if (sizeSpec && !(sizeSpec instanceof Size)) {
        // For SizeConstraints, calculate the MIP-adjusted cache size
        const mipStrategy = entry1.imageSource.scalable ? MIP.DOWN_OR_UP : MIP.DOWN;
        renderSize = calculateFittedSize(
          entry1.imageSource.intrinsicSize,
          sizeSpec,
          mipStrategy
        );
      }

      const image = entry1.imageSource.render(renderSize);
      entry2 = { image };
      this.#cache2.set(key, entry2);
    }

    // Return the cached image (may be larger than requested due to MIP scaling)
    // Final scaling to exact target size should happen during drawing/compositing
    return entry2.image;
  }

  clear(): void {
    this.#cache1.clear();
    this.#cache2.clear();
  }

  has(sourceId: string, sizeSpec?: SizeSpec): boolean {
    const entry1 = this.#cache1.get(sourceId);
    if (!entry1) {
      return false;
    }

    // If no sizeSpec provided, just check if the source is in level 1 cache
    if (sizeSpec === undefined) {
      return true;
    }

    // Check if the specific rendered size exists in level 2 cache
    const key = ImageCache.cache2Key(entry1.imageSource, sizeSpec);
    return this.#cache2.has(key);
  }

  delete(sourceId: string, sizeSpec?: SizeSpec): boolean {
    if (sizeSpec !== undefined) {
      // Delete specific render size - need to look up ImageSource from cache1
      const entry1 = this.#cache1.get(sourceId);
      if (!entry1) {
        return false; // Source not in cache
      }
      const key = ImageCache.cache2Key(entry1.imageSource, sizeSpec);
      return this.#cache2.delete(key);
    } else {
      // Delete all render sizes for this source
      const deleted = this.#cache1.delete(sourceId);

      // Clean up all sized versions
      const sizeKeys = Array.from(this.#cache2.keys()).filter(key =>
        key.startsWith(`${sourceId}-`)
      );
      sizeKeys.forEach(key => this.#cache2.delete(key));

      return deleted;
    }
  }

  getCacheStats(): { level1Size: number; level2Size: number } {
    return {
      level1Size: this.#cache1.size,
      level2Size: this.#cache2.size
    };
  }
}
